import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import PageContainer from '~/shared/components/PageContainer';
import Card from '~/shared/components/Card';
import Typography from '~/shared/components/Typography';
import ChartFilterDropdown from '~/fleet-manager/components/DashboardCharts/ChartFilterDropdown';
import Table from '~/shared/components/Table';
import { LogSelectionOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { OptLogVolumeActionsCountResponse, OptLogVolumeActionsResponse } from '~/shared/types/api';
import { apiFilterKey } from '~/fleet-manager/components/common/serachParamKeys';
import {
  CUSTOM_DATES_TITLE,
  dateFilterItems,
} from '~/fleet-manager/components/common/dateFilterMenuItems';
import { getDateFilterDates } from '~/shared/utils/getDateFilterDates';

import LastDay from '../LastDay/LastDay';
import LogFilter from '../LogFilter/LogFIlter';
import { getApiFilterFromSelectOption } from '../common/helpers';

import { getOperationColumnDefs } from './columnDefs';

const dataLimit = 50;

const sortKey = 'sort_field';

export default function LogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = JSON.parse(searchParams.get(apiFilterKey) || '[]') as LogSelectionOption[];
  const optimizationDateFilterKey = 'optimization_date_filter';
  const activeDateFilterSearchParams = searchParams.get(optimizationDateFilterKey);
  const sortField: { field: string; dir: 'desc' | 'asc' } = JSON.parse(
    searchParams.get(sortKey) as string
  );

  console.log(sortField);

  const [pageOffset, setPageOffset] = useState('0');
  const [dataCount, setDataCount] = useState(0);

  const activeDateFilter = dateFilterItems.find((f) =>
    activeDateFilterSearchParams
      ? f.title === activeDateFilterSearchParams
      : f.title === 'Last 3 hours'
  )!;

  const api = useXFetch();

  const [startDate, endDate] = getDateFilterDates(
    activeDateFilter.duration,
    activeDateFilterSearchParams
  );

  const qs = new URLSearchParams([
    ['start_date', startDate],
    ['end_date', endDate],
    [
      'sortBy',
      JSON.stringify({
        field: sortField?.field || 'actionFinishTime',
        ascending: sortField?.dir === 'asc',
      }),
    ],
    ['limit', dataLimit.toString()],
    ['offset', pageOffset],
  ]);

  const countQS = new URLSearchParams([
    ['start_date', startDate],
    ['end_date', endDate],
  ]);

  const { data } = useQuery({
    queryKey: [
      'getOptimizationActions',
      JSON.stringify({ filters, sortField, pageOffset, activeDateFilterSearchParams }),
    ],

    refetchOnWindowFocus: false,
    queryFn: async () => {
      filters.forEach((f) => {
        if (!qs.has('filters', getApiFilterFromSelectOption(f))) {
          qs.append('filters', getApiFilterFromSelectOption(f));
        }
        if (!countQS.has('filters', getApiFilterFromSelectOption(f))) {
          countQS.append('filters', getApiFilterFromSelectOption(f));
        }
      });

      const volumes = await api<OptLogVolumeActionsResponse>(
        `/api/v1/opt-log/actions?${qs.toString()}`
      );

      if (volumes.actions === null) {
        volumes.actions = [];
      }

      const { count } = await api<OptLogVolumeActionsCountResponse>(
        `/api/v1/opt-log/actions/count?${countQS.toString()}`
      );

      if (count && !isNaN(count)) {
        setDataCount(count);
      }

      return { actions: volumes?.actions || [], ...{ count } };
    },
  });

  const operationColumnDefs = useMemo(() => getOperationColumnDefs({ sortField }), [sortField]);

  const onPageChange = (page: number): void => {
    setPageOffset(`${page * dataLimit}`);
  };

  const rowData = useMemo(() => data?.actions, [data?.actions]);

  return (
    <PageContainer>
      <div className="flex gap-[48px] items-start">
        <LogFilter
          filters={filters}
          setFilters={(
            value: LogSelectionOption[] | ((filter: LogSelectionOption[]) => LogSelectionOption[])
          ) => {
            const val = typeof value === 'function' ? value(filters) : value;

            qs.delete('filters');
            setSearchParams((params) => {
              params.set(apiFilterKey, JSON.stringify(val));
              return params;
            });
          }}
        />
      </div>

      <LastDay />

      <Card className="flex flex-col h-[628px] ">
        <div className="pb-[24px] w-full flex justify-between">
          <div className="h-[44.5px] flex items-center">
            <Typography>
              Optimization Actions{' '}
              {data?.count !== undefined ? (
                `(${data?.count})`
              ) : (
                <span>
                  ({<ArrowPathIcon className="h-6 w-4 animate-spin  stroke-[2px] inline-block" />})
                </span>
              )}
            </Typography>
          </div>
          <ChartFilterDropdown
            items={dateFilterItems}
            onSelect={(filter) => {
              setSearchParams(
                (params) => {
                  params.set(optimizationDateFilterKey, filter.title);
                  return params;
                },
                { replace: true }
              );
            }}
            selectedItem={
              activeDateFilterSearchParams &&
              activeDateFilterSearchParams.includes(CUSTOM_DATES_TITLE)
                ? { title: activeDateFilterSearchParams }
                : activeDateFilter
            }
          />
        </div>

        <Table
          rowData={rowData}
          columnDefs={operationColumnDefs}
          defaultColDef={{ filter: false }}
          headerHeight={40}
          onSortChange={(sort) => {
            setSearchParams(
              (params) => {
                params.set(sortKey, JSON.stringify(sort));
                return params;
              },
              { replace: true }
            );
          }}
          pagination={{
            perPage: dataLimit,
            onChange: onPageChange,
            totalPages: Math.ceil(dataCount / dataLimit),
          }}
        />
      </Card>
    </PageContainer>
  );
}
